import { z } from "zod";
import type { ActiveMedicalCase } from "./active-medical-case";
import { meetingReasonSchema, type MeetingReason } from "./meeting";

// TES-1168 (A-02, canonical addendum 2026-10-08, function 1) — a new care
// meeting for a specific patient, booked by care staff from the patient
// overview ("Boka nytt vårdmöte"), and the patient's own standalone
// "Boka ett vårdmöte" under Din vård.
//
// Both are separate from the onboarding booking (CP-01): a meeting booked
// here is its own current booking. It never reopens or restarts the
// onboarding step, never asks for F-02 again and never charges the
// onboarding fee a second time.

// ---------------------------------------------------------------------------
// Professions
// ---------------------------------------------------------------------------

// Who a meeting can be booked with. V1 is the doctor only ("I första versionen
// kan patienten boka läkare"); the decision says the function must later grow
// to other professions and competences. Adding one is a new entry here plus
// what the profession's meeting needs (the Daily token mint and the
// clinician's meeting list are doctor-only today), not a new booking path.
export const BOOKABLE_PROFESSIONS = ["doctor"] as const;
export const bookableProfessionSchema = z.enum(BOOKABLE_PROFESSIONS);
export type BookableProfession = z.infer<typeof bookableProfessionSchema>;

// The staff role that holds each profession's meetings.
export const PROFESSION_ROLE: Record<BookableProfession, "doctor"> = {
  doctor: "doctor",
};

// ---------------------------------------------------------------------------
// Meeting types staff may choose
// ---------------------------------------------------------------------------

// `first_consult` is deliberately absent: it IS the onboarding meeting, and
// its completion advances the onboarding journey (journeyState, CP-04/CP-05).
// A replacement for a meeting Ella missed is booked as a follow-up in the same
// care case, so it carries none of that.
export const STAFF_BOOKABLE_MEETING_REASONS = [
  "follow_up",
  "renewal_review",
] as const satisfies readonly MeetingReason[];
export const staffBookableMeetingReasonSchema = z.enum(
  STAFF_BOOKABLE_MEETING_REASONS,
);
export type StaffBookableMeetingReason = z.infer<
  typeof staffBookableMeetingReasonSchema
>;

// The lengths offered in admin. A free integer would let a typo book a
// three-hour meeting; the list covers every length the clinic uses.
export const MEETING_DURATION_OPTIONS = [15, 20, 30, 45, 60] as const;

// ---------------------------------------------------------------------------
// Meeting products — the only way a staff-booked meeting carries a fee
// ---------------------------------------------------------------------------

// "Vid avgift väljs en fördefinierad och versionsstyrd mötesprodukt med
// fastställt pris; vårdpersonalen ska inte skriva ett godtyckligt belopp."
//
// The products are a fixed list, and their prices are version-controlled the
// way the blood test's is (TES-720): the amount lives in the tenant's pricing
// config in git, and is snapshotted on the offer the moment staff make it, so
// she pays what she was offered even if the price later changes. The charge
// goes through the same Stripe Price (lookup key, TES-1114) as the onboarding
// consultation, and a Price that disagrees with the snapshot refuses the
// payment instead of charging another sum.
export const MEETING_PRODUCT_KEYS = ["digital_care_meeting"] as const;
export const meetingProductKeySchema = z.enum(MEETING_PRODUCT_KEYS);
export type MeetingProductKey = z.infer<typeof meetingProductKeySchema>;

export const meetingProductSchema = z.object({
  key: meetingProductKeySchema,
  label: z.string(),
  amountMinor: z.number().int().nonnegative(),
  currency: z.string(),
  /**
   * The rule the patient pays under: she confirms and pays in the app before
   * the meeting becomes a booking, at the latest when it starts; cancelling
   * more than 24 hours ahead refunds the fee, like the onboarding meeting.
   */
  payBeforeStart: z.literal(true),
});
export type MeetingProduct = z.infer<typeof meetingProductSchema>;

// ---------------------------------------------------------------------------
// Staff: options + booking
// ---------------------------------------------------------------------------

export const staffMeetingProviderSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  profession: bookableProfessionSchema,
});
export type StaffMeetingProvider = z.infer<typeof staffMeetingProviderSchema>;

export const staffMeetingBookingOptionsSchema = z.object({
  providers: z.array(staffMeetingProviderSchema),
  reasons: z.array(staffBookableMeetingReasonSchema),
  durations: z.array(z.number().int().positive()),
  products: z.array(meetingProductSchema),
});
export type StaffMeetingBookingOptions = z.infer<
  typeof staffMeetingBookingOptionsSchema
>;

export const staffMeetingPaymentSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("free") }),
  z.object({ kind: z.literal("product"), productKey: meetingProductKeySchema }),
]);
export type StaffMeetingPayment = z.infer<typeof staffMeetingPaymentSchema>;

export const staffBookMeetingSchema = z.object({
  providerId: z.string().uuid(),
  reason: staffBookableMeetingReasonSchema,
  /** ISO instant of the start. */
  startAt: z.string().datetime({ offset: true }),
  durationMinutes: z
    .number()
    .int()
    .refine((n) => (MEETING_DURATION_OPTIONS as readonly number[]).includes(n)),
  payment: staffMeetingPaymentSchema,
});
export type StaffBookMeetingRequest = z.infer<typeof staffBookMeetingSchema>;

export const staffBookMeetingResultSchema = z.discriminatedUnion("kind", [
  // Free: booked at once, in the patient's app with time, provider and link.
  z.object({
    kind: z.literal("booked"),
    meetingId: z.string().uuid(),
    scheduledAt: z.string(),
  }),
  // Paid: an offer the patient confirms and pays before it becomes a booking.
  z.object({
    kind: z.literal("awaiting_payment"),
    offerId: z.string().uuid(),
    scheduledAt: z.string(),
  }),
]);
export type StaffBookMeetingResult = z.infer<
  typeof staffBookMeetingResultSchema
>;

// ---------------------------------------------------------------------------
// Meeting offers — a paid staff booking before the patient has paid
// ---------------------------------------------------------------------------

// TES-1199 (Ö-405) — `declined` is the patient saying no to the proposed
// time; `cancelled` is the clinic withdrawing it. Kept apart so the care team
// can see which one happened. Neither ends her care.
export const MEETING_OFFER_STATUSES = [
  "awaiting_payment",
  "booked",
  "cancelled",
  "declined",
  "expired",
] as const;
export const meetingOfferStatusSchema = z.enum(MEETING_OFFER_STATUSES);
export type MeetingOfferStatus = z.infer<typeof meetingOfferStatusSchema>;

// Admin's view of an offer on the patient page.
export const clinicianMeetingOfferSchema = z.object({
  id: z.string().uuid(),
  status: meetingOfferStatusSchema,
  scheduledAt: z.string(),
  durationMinutes: z.number().int(),
  reason: meetingReasonSchema,
  provider: z.object({ id: z.string().uuid(), displayName: z.string() }),
  product: meetingProductSchema.pick({
    key: true,
    label: true,
    amountMinor: true,
    currency: true,
  }),
  meetingId: z.string().uuid().nullable(),
  createdAt: z.string(),
});
export type ClinicianMeetingOffer = z.infer<typeof clinicianMeetingOfferSchema>;

// The patient's view: what she is asked to confirm and pay.
export const patientMeetingOfferSchema = z.object({
  id: z.string().uuid(),
  scheduledAt: z.string(),
  durationMinutes: z.number().int(),
  reason: meetingReasonSchema,
  provider: z.object({ id: z.string().uuid(), displayName: z.string() }),
  product: meetingProductSchema.pick({
    key: true,
    label: true,
    amountMinor: true,
    currency: true,
  }),
  /** Paid but not yet confirmed (the webhook settled it first): confirm books it. */
  paid: z.boolean(),
});
export type PatientMeetingOffer = z.infer<typeof patientMeetingOfferSchema>;

export const confirmMeetingOfferResultSchema = z.discriminatedUnion("booked", [
  z.object({
    booked: z.literal(true),
    meetingId: z.string().uuid(),
    scheduledAt: z.string(),
  }),
  // The Payment Sheet has not settled yet; the app polls confirm again.
  z.object({ booked: z.literal(false) }),
]);
export type ConfirmMeetingOfferResult = z.infer<
  typeof confirmMeetingOfferResultSchema
>;

// ---------------------------------------------------------------------------
// Patient: the standalone "Boka ett vårdmöte" under Din vård
// ---------------------------------------------------------------------------

// Which professions she can book herself right now, and if not, why. The app
// renders one entry per profession, so a new one appears without a client
// change to the list logic.
export const careMeetingBookingBlockerSchema = z.enum([
  // Legacy, for builds before CLIENT_CONTRACT_CHANGES.paidCareMeeting: they
  // can only book a meeting her subscription includes (TES-643), so a meeting
  // she would pay for is shown to them as this. Never sent to a current build.
  "subscription_required",
  // Legacy (TES-851), no longer sent: TES-1196 (Ö-398) lets a prescription
  // transfer book a voluntary meeting under the same rule as anyone else.
  "transfer_meeting_not_required",
  // TES-1196 (Ö-397): her medical case has ended. Since TES-1555 (Ö-429) only
  // a documented ended care relationship, or a deleted account, sends it: no
  // general "slutligt stängt" status blocks booking. The wire value stays,
  // because app 2.0.11 ships its own line for it.
  "medical_case_closed",
  // Nothing medical has started ("Konto/abonnemang ensamt"): her way to a
  // doctor is onboarding's own booking, not this one.
  "no_medical_case",
  // Onboarding's first meeting (CP-01) is still ahead of her: a new patient
  // before her consult, or a transfer whose doctor decided a meeting is
  // required. That meeting is booked from onboarding, never from here.
  "onboarding_meeting_pending",
]);
export type CareMeetingBookingBlocker = z.infer<
  typeof careMeetingBookingBlockerSchema
>;

// TES-1196 (Ö-397, Ö-401) — what a meeting she can book costs her: included
// in her subscription, or the fixed-price product. One or the other, never
// both: "Patienten får inte möta båda budskapen samtidigt."
export const careMeetingPaymentSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("included") }),
  z.object({
    kind: z.literal("paid"),
    product: meetingProductSchema.pick({
      key: true,
      label: true,
      amountMinor: true,
      currency: true,
    }),
  }),
]);
export type CareMeetingPayment = z.infer<typeof careMeetingPaymentSchema>;

export const careMeetingOptionsSchema = z.object({
  professions: z.array(
    z.object({
      profession: bookableProfessionSchema,
      /** The meeting reason the booking is sent with. */
      reason: meetingReasonSchema,
      bookable: z.boolean(),
      blocker: careMeetingBookingBlockerSchema.nullable(),
      /**
       * TES-1222 — why she cannot book, as the patient reads it, rendered by
       * the server in her preferred locale so the copy can change without an
       * app release. Null when nothing blocks her, and for the legacy
       * blockers (an older build renders those itself). Absent from an API
       * older than the change; an app before 2.0.9 ignores it.
       */
      blockerText: z.string().nullable().optional(),
      /**
       * TES-1196 — set exactly when `bookable`, null otherwise. Absent from
       * an API older than the change.
       */
      payment: careMeetingPaymentSchema.nullable().optional(),
    }),
  ),
});
export type CareMeetingOptions = z.infer<typeof careMeetingOptionsSchema>;

// ---------------------------------------------------------------------------
// TES-1196 — who may book "Boka ett vårdmöte" herself, and at what price
// ---------------------------------------------------------------------------

// Ö-397 (Emelie 2026-10-08): "En patient utan aktivt abonnemang ska kunna
// boka ett frivilligt vårdmöte för 395 kr, så länge hennes medicinska ärende
// inte är slutligt stängt. Den här möjligheten är separat från det första
// vårdmötet i onboarding och från möten som ingår i ett aktivt abonnemang."
// Ö-398: "Receptövertagspatienten ska kunna boka ett frivilligt vårdmöte."
//
// TES-1555 S2 (Ö-429, Emelie 2026-10-21): "Ingen övergripande patientstatus
// Slutligt stängt medicinskt ärende som i sig blockerar bokning. … Bokning/pris
// hanteras per verklig mötestyp, inte en generell 395-kronorstaxa."
//
// In order:
//   1. a subscription that includes meetings: free;
//   2. a documented ended care relationship, or a deleted account: refused.
//      Whether an ended care relationship should refuse is Ö-876 (open);
//      built as the plan's recommendation (a): she contacts Ella, and a
//      doctor starts a new care process;
//   3. nothing medical has started: refused, her first meeting is
//      onboarding's;
//   4. onboarding's first meeting still ahead: refused, it is booked there;
//   5. otherwise: the product for the meeting she books. That includes a
//      paused treatment ("Ett pausat ärende är fortfarande aktivt"), an
//      ended drug treatment, and a rejected application, which is an
//      assessment outcome, not an ending (Ö-411).
// A transfer patient takes the same path; nothing here asks for her group.
export interface CareMeetingEligibilityFacts {
  /** Her subscription is trialing or active (TES-591's content gate). */
  subscriptionIncludesMeetings: boolean;
  /** activeMedicalCase(...) for her, as TES-1200 defines it. */
  medicalCase: ActiveMedicalCase;
  /**
   * Onboarding's first meeting has not been held and is hers to book from
   * onboarding: a NEW_TREATMENT patient with no consult started or held, or
   * a transfer with an open MEETING_REQUIRED decision.
   */
  onboardingMeetingPending: boolean;
}

export type CareMeetingEligibility =
  | { kind: "included" }
  | { kind: "paid" }
  | { kind: "blocked"; blocker: CareMeetingBookingBlocker };

export function careMeetingEligibility(
  f: CareMeetingEligibilityFacts,
): CareMeetingEligibility {
  if (f.subscriptionIncludesMeetings) return { kind: "included" };
  if (!f.medicalCase.active) {
    switch (f.medicalCase.reason) {
      case "care_relationship_ended":
      case "account_deleted":
        return { kind: "blocked", blocker: "medical_case_closed" };
      case "no_medical_case":
        return { kind: "blocked", blocker: "no_medical_case" };
      case "assessment_rejected":
        break;
    }
  }
  if (f.onboardingMeetingPending) {
    return { kind: "blocked", blocker: "onboarding_meeting_pending" };
  }
  return { kind: "paid" };
}

// ---------------------------------------------------------------------------
// TES-1196 — the patient books "Boka ett vårdmöte" herself
// ---------------------------------------------------------------------------

// POST patient-api/v1/bookings/care-meeting: a slot from the profession's
// list. The server decides what it costs when she books; the client sends
// neither a price nor a reason.
export const selfBookCareMeetingSchema = z.object({
  doctorId: z.string().uuid(),
  startAt: z.string().datetime({ offset: true }),
});
export type SelfBookCareMeetingRequest = z.infer<
  typeof selfBookCareMeetingSchema
>;

// How long a time she picked but has not paid for stays hers. Long enough for
// the terms and the Payment Sheet; short enough that an abandoned payment
// does not keep the provider's time from everyone else.
export const SELF_BOOKED_MEETING_HOLD_MINUTES = 15;

export const selfBookCareMeetingResultSchema = z.discriminatedUnion("kind", [
  // Included in her subscription: booked at once.
  z.object({
    kind: z.literal("booked"),
    meetingId: z.string().uuid(),
    scheduledAt: z.string(),
  }),
  // The fixed-price product: the time is held while she confirms and pays
  // through the meeting-offer payment routes (by `offerId`); paid, it is
  // booked.
  z.object({
    kind: z.literal("awaiting_payment"),
    offerId: z.string().uuid(),
    scheduledAt: z.string(),
    holdExpiresAt: z.string(),
  }),
]);
export type SelfBookCareMeetingResult = z.infer<
  typeof selfBookCareMeetingResultSchema
>;
